import {useDataTable} from '@/hooks/useDataTable-hook';
import {GetFormattedSalesTargetData} from '@/models/SalesTargetListModel';
import {SalesTargetsColConfig} from '@/services/MockData/SalesTargets/SalesTargetsColConfig';
import {getSalesTargetsList} from '@/services/salesTargets';
import {useQueries, keepPreviousData} from '@tanstack/react-query';
import {buildQueryParams} from '@/utils/commons';
import {useMemo, useRef, useState} from 'react';

const initialSortConfig = {
  column: SalesTargetsColConfig[8].name_of_db_column,
  direction: 'desc'
};

/**
 * Hook for sales targets data fetching, pagination, sorting, and search state.
 * @param {boolean} enableQuery - Whether to enable the sales targets list query.
 */
export const useSalesTargetsQuery = (enableQuery = true) => {
  const [pageSize, setPageSize] = useState(10);

  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);

  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex, pageSize]);

  const isValidDateRange = useMemo(() => {
    if (!searchArr?.length) return true;
    const dateRangeSearch = searchArr.find((s) => s.type === 'dateRange');
    if (!dateRangeSearch) return true;
    const [startDate, endDate] = dateRangeSearch.value.split(',');
    return Boolean(
      startDate && endDate && !dateRangeSearch.value.endsWith(',')
    );
  }, [searchArr]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['SALES_TARGETS', queryParams, searchArr],
        queryFn: () => getSalesTargetsList(queryParams, searchArr),
        enabled: enableQuery && isValidDateRange,
        placeholderData: keepPreviousData
      }
    ]
  });

  const resultsData = useMemo(() => data || {}, [data]);

  const parsedSalesTargetsData = useMemo(
    () => GetFormattedSalesTargetData(resultsData),
    [resultsData]
  );

  // Rapid page/filter changes can break the keepPreviousData chain, leaving
  // `data` briefly undefined mid-fetch. Serve the last good payload during
  // that gap; settled results always pass through.
  const lastGoodDataRef = useRef(null);
  const salesTargetsData = useMemo(() => {
    const hasRows = parsedSalesTargetsData?.data?.length > 0;
    if (hasRows || !isFetching) {
      lastGoodDataRef.current = parsedSalesTargetsData;
      return parsedSalesTargetsData;
    }
    return lastGoodDataRef.current ?? parsedSalesTargetsData;
  }, [parsedSalesTargetsData, isFetching]);

  // Some responses omit totalRecords on later pages — hold the last known
  // total while rows exist; a genuinely empty result resets it.
  const lastKnownTotalRef = useRef(0);
  const totalRecords = useMemo(() => {
    const parsed = Number(salesTargetsData?.totalRecords);
    if (Number.isFinite(parsed) && parsed > 0) {
      lastKnownTotalRef.current = parsed;
      return parsed;
    }
    return salesTargetsData?.data?.length > 0 ? lastKnownTotalRef.current : 0;
  }, [salesTargetsData]);

  return {
    salesTargetsColConfigData: SalesTargetsColConfig,
    salesTargetsData,
    totalRecords,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    searchArr,
    isLoading,
    isFetching,
    handleResetAll,
    isValidDateRange,
    queryParams
  };
};
